import React from 'react';
import {
  Button, ChatBubble, ChatComposer, ChatDayDivider, ChatThread,
  EmptyState, SearchInput, useKeyboardInset, useToast,
} from '@parsina/framework';
import { ConversationListSkeleton, ThreadSkeleton } from './MessagesSkeleton';
import { FilterX, Inbox, MessageSquare, SearchX } from 'lucide-react';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import SegmentedControl from '../../components/common/SegmentedControl';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import MobileHeader from '@parsina/framework/clinical/src/components/mobile/MobileHeader';
import MobileConversationCard from '../../components/mobile/MobileConversationCard';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { MESSAGE_STATUS, messageStatusOf } from '../../constants/messageStatus';
import { useMessages } from './hooks/useMessages';
import { groupTimelineByDay, readOnlyNoticeKey } from './Messages.shared';

// ─────────────────────────────────────────────────────────────────────────────
// Messages — the phone surface (DESIGN-CONCEPT-MOBILE v2).
//
// Two screens, not two panes, and `?c=` is the boundary between them:
//
//   no ?c=  §3.1 top-level destination — cockpit header, bottom nav visible
//           (App owns that), the conversation list.
//   ?c=     §4B content screen — content-mode header whose back clears `?c=`
//           and whose title is the counterpart, NO bottom nav, and the composer
//           docked above the home indicator. `ChatThread` runs WITHOUT `fill`:
//           the message column is page flow, per the mobile page-scroll rule.
// ─────────────────────────────────────────────────────────────────────────────

const SEARCH_DEBOUNCE_MS = 300;

// Client-side list window: the conversation list grows through an explicit
// «نمایش بیشتر» expander — never infinite scroll (mobile concept §4). The
// directory can return 200 conversations plus 200 contact stubs; a phone must
// not be handed 400 cards.
const PAGE_SIZE = 10;

const PAGE = {
  position: 'absolute', inset: 0,
  display: 'flex', flexDirection: 'column',
  overflow: 'hidden', background: C.bg,
};

// ── Thread screen ────────────────────────────────────────────────────────────

const ThreadScreen = ({ m }) => {
  const { t, formatTime, formatDate } = useTranslation();
  const toast = useToast();
  const [draft, setDraft] = React.useState('');
  // `PAGE` is `position:absolute; inset:0`, and the layout viewport does NOT
  // shrink when the software keyboard opens on iOS — without this the composer
  // sits under the keys the doctor is typing on.
  const keyboardInset = useKeyboardInset();

  React.useEffect(() => { setDraft(''); }, [m.selectedRoomId]);

  const onSend = async () => {
    const ok = await m.send(draft);
    if (!ok) toast.error(t('messages.errors.send'));
    else setDraft('');
  };

  const groups = React.useMemo(() => groupTimelineByDay(m.timeline), [m.timeline]);

  const state = m.chatError ? 'error'
    : !m.chatReady ? 'loading'
      : m.timeline.length === 0 ? 'empty' : 'content';

  const title = m.selected?.peerDisplayName || t('messages.unknownContact');

  return (
    <div style={PAGE}>
      <div style={{ flexShrink: 0 }}>
        <MobileHeader
          title={title}
          onBack={m.clearSelection}
          backTestId="messages-header-back"
          backLabel={t('messages.title')}
        />
      </div>

      {/* Page scroll for the message column (ChatThread without `fill`), and
          the node that owns the region's §7 state in all four states.
          `messages-thread` stays on whatever renders the messages — the kit
          derives `messages-thread-load-older` from it, and owns its own
          data-state. */}
      <div
        data-testid="messages-thread-region"
        data-state={state}
        aria-busy={state === 'loading' || undefined}
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
      >
        {state === 'content' ? (
          <ChatThread
            // Per-conversation scroll state: a room switch builds a new thread
            // rather than opening the next conversation where the last one was.
            key={m.selectedRoomId}
            data-testid="messages-thread"
            hasOlder={m.hasOlder}
            loadingOlder={m.loadingOlder}
            onLoadOlder={m.loadOlder}
            loadOlderLabel={t('messages.thread.loadOlder')}
            padBlockRem={0.75}
          >
            {groups.flatMap((group) => [
              <ChatDayDivider key={`d-${group.dayTs}`} label={formatDate(group.dayTs, 'medium')} />,
              ...group.messages.map((msg) => {
                const statusKey = messageStatusOf(msg);
                return (
                  <ChatBubble
                    key={msg.txnId || msg.eventId}
                    data-testid={`messages-bubble-${msg.txnId || msg.eventId}`}
                    body={msg.body}
                    mine={msg.mine}
                    pending={msg.pending}
                    failed={msg.failed}
                    timeLabel={formatTime(msg.ts)}
                    // The kit draws the glyph; the app supplies only the word
                    // for it. No app-side dot — a hue is not a delivery state.
                    status={msg.mine ? MESSAGE_STATUS[statusKey].bubble : undefined}
                    statusLabel={msg.mine ? t(MESSAGE_STATUS[statusKey].labelKey) : undefined}
                    onRetry={msg.failed ? () => m.retry(msg.txnId) : undefined}
                    retryLabel={t('messages.retry')}
                  />
                );
              }),
            ])}
          </ChatThread>
        ) : (
          <div
            data-testid="messages-thread"
            data-state={state}
            style={{ padding: '0.875rem' }}
          >
            {state === 'error' ? (
              <ErrorState
                error={m.chatError}
                title={t('messages.thread.unavailable')}
                onRetry={m.reloadThread}
              />
            ) : state === 'loading' ? (
              <ThreadSkeleton />
            ) : (
              <EmptyState
                icon={<MessageSquare size={48} color={C.textMuted} strokeWidth={1.5} aria-hidden />}
                title={t('messages.thread.empty.title')}
                description={t('messages.thread.empty.hint')}
              />
            )}
          </div>
        )}
      </div>

      {/* Docked composer — clear of the home indicator (§6) AND of the software
          keyboard, which is painted OVER the layout viewport on iOS. */}
      {state !== 'error' && (
        <div
          data-testid="messages-composer-dock"
          style={{
            flexShrink: 0,
            paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${keyboardInset}px)`,
          }}
        >
          <ChatComposer
            data-testid="messages-composer"
            inputProps={{ 'data-testid': 'messages-composer-input' }}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onSend={onSend}
            sending={m.sending}
            placeholder={t('messages.composer.placeholder')}
            sendLabel={t('messages.composer.send')}
            readOnlyNotice={m.canSend ? undefined : t(readOnlyNoticeKey(m.readOnlyReason))}
          />
        </div>
      )}
    </div>
  );
};

// ── List screen ──────────────────────────────────────────────────────────────

const ListScreen = ({ m, onOpen }) => {
  const { t } = useTranslation();
  const [search, setSearch] = React.useState(m.query);
  const debounceRef = React.useRef(null);

  React.useEffect(() => { setSearch(m.query); }, [m.query]);
  React.useEffect(() => () => clearTimeout(debounceRef.current), []);

  const onSearch = (value) => {
    setSearch(value);
    clearTimeout(debounceRef.current);
    if (value === '') { m.setSearch(''); return; }
    debounceRef.current = setTimeout(() => m.setSearch(value), SEARCH_DEBOUNCE_MS);
  };

  const clear = () => { setSearch(''); m.clearFilters(); };

  const isEmpty = !m.loading && !m.error && m.rows.length === 0;
  const state = m.loading ? 'loading'
    : m.error ? 'error'
      : isEmpty ? 'empty' : 'content';
  // Which EMPTY it is only changes the copy — `data-state` keeps the four
  // enumerated values (§15.1), so a [data-state="empty"] selector still
  // matches with a filter on. The app's own Encounters/Accounting screens do
  // exactly this.
  const filteredEmpty = isEmpty && m.hasActiveFilters;

  // The window resets whenever the SET changes — a new filter must not open on
  // page four of the previous one.
  const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);
  React.useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [m.query, m.unreadOnly, m.rows.length]);
  const visibleRows = m.rows.slice(0, visibleCount);
  const hiddenCount = Math.max(0, m.rows.length - visibleCount);

  return (
    <div style={PAGE}>
      <div style={{ flexShrink: 0 }}>
        <MobileHeader title={t('messages.title')} />
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '1rem' }}>
        {/* Toolbar (zone ④) — one card row. */}
        <div style={{ padding: '0.875rem 0.875rem 0' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.625rem', flexWrap: 'wrap',
            background: C.card, border: `1px solid ${C.border}`, borderRadius: '0.875rem',
            padding: '0.625rem 0.75rem',
          }}>
            <SearchInput
              data-testid="messages-toolbar-search"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={t('messages.search.placeholder')}
              clearLabel={t('common.actions.clearSearch')}
              style={{ flex: 1, minWidth: '8rem' }}
            />
            <SegmentedControl
              testId="messages-filter"
              ariaLabel={t('messages.filters.label')}
              items={[
                { id: 'all', label: t('messages.filters.all') },
                { id: 'unread', label: t('messages.filters.unread') },
              ]}
              value={m.unreadOnly ? 'unread' : 'all'}
              onChange={(id) => m.setUnreadOnly(id === 'unread')}
            />
            {m.hasActiveFilters && (
              <Button variant="ghost" size="sm" className="min-h-11" onClick={clear} data-testid="messages-toolbar-clear">
                <FilterX size={15} aria-hidden />
                {t('messages.clearFilters')}
              </Button>
            )}
          </div>
        </div>

        <div
          data-testid="messages-list"
          data-state={state}
          aria-label={t('messages.list.label')}
          aria-busy={m.loading || m.refreshing || undefined}
          data-refreshing={m.refreshing ? 'true' : undefined}
          style={{
            padding: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.625rem',
            // A refetch dims the rows; it never takes them away (§2.4).
            opacity: m.refreshing ? 0.45 : 1,
            transition: 'opacity 0.15s',
          }}
        >
          {state === 'loading' && <ConversationListSkeleton count={4} avatar={44} />}

          {state === 'error' && (
            <ErrorState
              title={t('messages.list.unavailable')}
              error={m.error}
              onRetry={m.reload}
            />
          )}

          {state === 'empty' && filteredEmpty && (
            <EmptyState
              icon={<SearchX size={48} color={C.textMuted} strokeWidth={1.5} aria-hidden />}
              title={t('messages.list.filteredEmpty.title')}
              description={t('messages.list.filteredEmpty.hint')}
              action={
                <Button variant="outline" size="sm" className="min-h-11" onClick={clear} data-testid="messages-list-empty-clear">
                  {t('messages.clearFilters')}
                </Button>
              }
            />
          )}

          {state === 'empty' && !filteredEmpty && (
            <EmptyState
              icon={<Inbox size={48} color={C.textMuted} strokeWidth={1.5} aria-hidden />}
              title={t('messages.list.empty.title')}
              description={t('messages.list.empty.hint')}
            />
          )}

          {state === 'content' && visibleRows.map((row) => (
            <MobileConversationCard
              key={row.roomId || row.key}
              row={row}
              onOpen={onOpen}
              testId={`messages-list-row-${row.roomId || row.key}`}
            />
          ))}

          {state === 'content' && hiddenCount > 0 && (
            <Button
              variant="outline"
              data-testid="messages-show-more"
              className="w-full min-h-11"
              onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
            >
              {t('messages.list.showMore', { count: hiddenCount })}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Screen ───────────────────────────────────────────────────────────────────

export default function MessagesMobile() {
  const { t } = useTranslation();
  const toast = useToast();
  const m = useMessages();

  const onOpen = async (row) => {
    try {
      await m.openConversation(row);
    } catch (err) {
      console.error('openChatConversation failed:', err);
      toast.error(t('messages.errors.open'));
    }
  };

  // ONE screen at a time — the other is not rendered at all (§1.1), so the
  // list is not a hidden, still-tabbable layer under the open conversation.
  return m.selectedRoomId
    ? <ThreadScreen m={m} />
    : <ListScreen m={m} onOpen={onOpen} />;
}
